(() => {
  'use strict';

  const SELECTOR = '[data-faj-testimonials]';
  const GLOBAL_KEY = 'FAJTestimonialsSlider';

  const toBoolean = (value) => String(value) === 'true';
  const pad = (number) => String(number + 1).padStart(2, '0');

  function waitForFlickity(timeout) {
    return new Promise((resolve) => {
      if (window.Flickity) {
        resolve(window.Flickity);
        return;
      }

      if (timeout <= 0) {
        resolve(null);
        return;
      }

      const start = Date.now();
      const timer = window.setInterval(() => {
        if (window.Flickity) {
          window.clearInterval(timer);
          resolve(window.Flickity);
          return;
        }

        if (Date.now() - start >= timeout) {
          window.clearInterval(timer);
          resolve(null);
        }
      }, 100);
    });
  }

  async function initTestimonials(root) {
    if (!root || root.dataset.ftslInitialized === 'true') return;

    const track = root.querySelector('[data-ftsl-track]');
    const slides = Array.from(root.querySelectorAll('[data-ftsl-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-ftsl-dot]'));
    const prev = root.querySelector('[data-ftsl-prev]');
    const next = root.querySelector('[data-ftsl-next]');
    const current = root.querySelector('[data-ftsl-current]');

    if (!track || slides.length < 2) {
      root.dataset.ftslInitialized = 'true';
      return;
    }

    root.dataset.ftslInitialized = 'true';

    const autoplayEnabled = toBoolean(root.dataset.autoplay);
    const autoplaySpeed = Number(root.dataset.autoplaySpeed || 6000);
    const wrapAround = toBoolean(root.dataset.wrapAround);
    const pauseOnHover = toBoolean(root.dataset.pauseHover);
    const draggable = toBoolean(root.dataset.draggable);
    const pauseOffscreen = toBoolean(root.dataset.pauseOffscreen);
    const transitionSpeed = Number(root.dataset.transitionSpeed || 650);
    const flickityWait = Number(root.dataset.flickityWait || 5000);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const Flickity = await waitForFlickity(flickityWait);

    if (!Flickity) {
      root.classList.add('ftsl-section--static');
      return;
    }

    const flickity = new Flickity(track, {
      cellSelector: '[data-ftsl-slide]',
      cellAlign: 'left',
      contain: true,
      wrapAround,
      draggable,
      prevNextButtons: false,
      pageDots: false,
      adaptiveHeight: true,
      accessibility: true,
      imagesLoaded: true,
      selectedAttraction: reducedMotion ? 1 : 0.018,
      friction: reducedMotion ? 1 : 0.28,
      autoPlay: autoplayEnabled && !reducedMotion ? autoplaySpeed : false,
      pauseAutoPlayOnHover: pauseOnHover
    });

    track.style.setProperty('--ftsl-transition-speed', `${transitionSpeed}ms`);

    function update(index) {
      slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === index;
        slide.classList.toggle('is-selected', selected);
        slide.setAttribute('aria-hidden', selected ? 'false' : 'true');
      });

      dots.forEach((dot, dotIndex) => {
        const selected = dotIndex === index;
        dot.classList.toggle('is-active', selected);
        dot.setAttribute('aria-current', selected ? 'true' : 'false');
      });

      if (current) current.textContent = pad(index);

      if (!wrapAround) {
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      }
    }

    flickity.on('ready', () => update(flickity.selectedIndex));
    flickity.on('select', () => update(flickity.selectedIndex));
    flickity.on('settle', () => flickity.resize());

    prev?.addEventListener('click', () => flickity.previous(wrapAround));
    next?.addEventListener('click', () => flickity.next(wrapAround));

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.ftslDot || 0);
        flickity.select(index, wrapAround);
      });
    });

    let observer = null;

    if (pauseOffscreen && autoplayEnabled && !reducedMotion && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              flickity.playPlayer();
            } else {
              flickity.pausePlayer();
            }
          });
        },
        { threshold: 0.2 }
      );

      observer.observe(root);
    }

    const onVisibility = () => {
      if (!autoplayEnabled || reducedMotion) return;

      if (document.hidden) {
        flickity.pausePlayer();
      } else if (!observer) {
        flickity.playPlayer();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);

    const onBlockSelect = (event) => {
      const block = slides.find((slide) => slide.dataset.blockId === event.detail.blockId);
      if (!block) return;

      const index = slides.indexOf(block);
      flickity.select(index, false, true);
      flickity.pausePlayer();
    };

    const onBlockDeselect = () => {
      if (autoplayEnabled && !reducedMotion) flickity.playPlayer();
    };

    root.addEventListener('shopify:block:select', onBlockSelect);
    root.addEventListener('shopify:block:deselect', onBlockDeselect);

    root.ftslDestroy = () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      root.removeEventListener('shopify:block:select', onBlockSelect);
      root.removeEventListener('shopify:block:deselect', onBlockDeselect);
      flickity.destroy();
      delete root.dataset.ftslInitialized;
    };

    update(flickity.selectedIndex);
  }

  function initAll(scope = document) {
    if (scope.matches?.(SELECTOR)) initTestimonials(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initTestimonials);
  }

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initAll();
    return;
  }

  window[GLOBAL_KEY] = {
    init: initTestimonials,
    initAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(SELECTOR)
      ? event.target
      : event.target.querySelector?.(SELECTOR);

    root?.ftslDestroy?.();
  });
})();
